import React from 'react';
import { Box } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

// Helper function to determine if a color is light or dark
function isLightColor(color) {
  if (!color) return true; // Default to light
  
  let r, g, b;
  
  // Handle hex colors (#fff, #ffffff)
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return true; // Default to light if invalid hex
    }
  } else {
    // Handle rgba/rgb colors
    const rgbMatch = color.match(/\d+/g);
    if (!rgbMatch || rgbMatch.length < 3) return true;
    
    r = parseInt(rgbMatch[0]);
    g = parseInt(rgbMatch[1]);
    b = parseInt(rgbMatch[2]);
  }
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5; // If luminance > 0.5, it's a light color
}

const MobileFrame = ({ 
  children, 
  width = '340px', 
  height = '720px', 
  scale = false, 
  style = {}, 
  showBackButton = false, 
  onBackClick = () => {},
  backgroundColor = '#ffffff' // Default to white background
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        transformOrigin: 'center center',
        transform: scale ? 'scale(0.7)' : 'none',
      }}
    >
      {/* Mobile Frame */}
      <Box
        sx={{
          width: width,
          minWidth: width,
          maxWidth: width,
          height: height,
          boxSizing: 'border-box',
          minHeight: height,
          border: '10px solid #1a1a1a',
          overflow: 'hidden',
          borderRadius: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: '#ffffff',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Notch at the top */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            width: '130px',
            height: '20px',
            backgroundColor: '#1a1a1a',
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            zIndex: 4000,
          }}
        />

        {/* Screen Content */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            id="mobile-frame-scroll-container"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflowY: 'scroll',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              boxSizing: 'border-box',
              width: '100%',
              padding: '20px',
              paddingTop: showBackButton ? '0px' : '40px', // Adjust padding if back button is shown
              ...style,
            }}
          >
            {/* Header with back button */}
            {showBackButton && (() => {
              // Determine if background is light or dark for adaptive button styling
              const isLightBg = isLightColor(backgroundColor);
              const circleColor = isLightBg ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.5)';
              const circleColorHover = isLightBg ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.6)';
              const iconColor = isLightBg ? '#000000' : '#ffffff';

              return (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  paddingTop: '20px', // Account for notch
                  flexShrink: 0,
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'transparent',
                  zIndex: 1001,
                  marginBottom: '0px',
                  width: '100%',
                  pointerEvents: 'auto'
                }}>
                  <Box 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onBackClick) onBackClick();
                    }}
                    className={'logo_container'} 
                    sx={{ 
                      display: "flex", 
                      justifyContent: "center", 
                      alignItems: 'center',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: circleColor || 'rgba(0, 0, 0, 0.5)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': { backgroundColor: circleColorHover || 'rgba(0, 0, 0, 0.6)' },
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      flexShrink: 0
                    }}>
                    <ChevronLeftIcon
                      style={{
                        fontSize: '26px',
                        color: iconColor || '#ffffff',
                        cursor: 'pointer',
                        pointerEvents: 'none'
                      }}
                    />
                  </Box>
                </Box>
              );
            })()}
            <div style={{ marginTop: showBackButton ? '0px' : '-50px' }}>
            {children}
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileFrame;

